import type { OverallScoreData, OutreachPerformanceData } from '../types'

export const mockOverallScoreData: OverallScoreData = {
   teamName: 'KIyle Robinson Group',
   dateRange: {
      startDate: 'June 1',
      endDate: 'current',
   },
   score: {
      current: 700,
      maximum: 1400,
      changePercentage: 12,
      isPositiveChange: true,
   },
   performanceLevel: 'Average Performer',
   comparisonPercentage: 58,
   progressPercentage: 50,
}

export const sampleOutreachData: OutreachPerformanceData = {
   title: 'Outreach & Appointment Performance',
   metrics: {
      conversationsToAppointment: 3.5,
      comparisonToTopTeam: 1.4,
      phoneCallAttempts: 14.7,
      averageCallAttemptsPerLead: 2.9,
   },
   highlights: {
      topAppointmentCloser: {
         id: '1',
         name: 'Tyree Dicks',
         initials: 'TD',
         avatar: '/api/placeholder/40/40',
         conversationsPerAppointment: 2.9,
         callAttemptsPerConversation: 5.2,
      },
      strugglingAppointmentCloser: {
         id: '2',
         name: 'Patty Hatab',
         initials: 'PH',
         conversationsPerAppointment: 15.8,
         callAttemptsPerConversation: 2.5,
      },
      mostEffectiveConversationalist: {
         id: '3',
         name: 'Patty Hatab',
         initials: 'PH',
         conversationsPerAppointment: 15.8,
         callAttemptsPerConversation: 2.5,
      },
      strugglingToConnect: {
         id: '4',
         name: 'Emily G',
         initials: 'EG',
         conversationsPerAppointment: 8.2,
         callAttemptsPerConversation: 55.3,
      },
   },
   teamMembers: [
      {
         id: '1',
         name: 'Tyree Dicks',
         initials: 'TD',
         avatar: '/api/placeholder/40/40',
         conversationsPerAppointment: 2.9,
         callAttemptsPerConversation: 5.2,
      },
      {
         id: '2',
         name: 'Patty Hatab',
         initials: 'PH',
         conversationsPerAppointment: 15.8,
         callAttemptsPerConversation: 2.5,
      },
      {
         id: '3',
         name: 'Emily G',
         initials: 'EG',
         conversationsPerAppointment: 8.2,
         callAttemptsPerConversation: 55.3,
      },
      {
         id: '4',
         name: 'John Smith',
         initials: 'JS',
         conversationsPerAppointment: 4.1,
         callAttemptsPerConversation: 12.3,
      },
      {
         id: '5',
         name: 'Sarah Wilson',
         initials: 'SW',
         conversationsPerAppointment: 6.7,
         callAttemptsPerConversation: 8.9,
      },
      {
         id: '6',
         name: 'Mike Johnson',
         initials: 'MJ',
         conversationsPerAppointment: 5.3,
         callAttemptsPerConversation: 15.7,
      },
      {
         id: '7',
         name: 'Lisa Brown',
         initials: 'LB',
         conversationsPerAppointment: 7.2,
         callAttemptsPerConversation: 11.4,
      },
   ],
   proTip:
      'Appointment performance is one of the key leading indicators of success for real estate teams. Make sure you have set outreach expectations and tracking in place!',
   footerMessage:
      "Want to see how the entire team is doing with appointments? Forward flow's full release is in Q2 2025!",
   ctaText: 'Book a demo',
}

export interface LeadData {
   name: string
   value: number
   percentage: number
   color: string
}

export interface AlertMetrics {
   title: string
   question: string
   alertPercentage: string
   alertType: 'warning' | 'error' | 'info'
   description: string
   dateRange: string
}

export interface PerformanceMetrics {
   totalLeads: number
   appointmentsSet: number
   appointmentRate: number
   comparisonRate: number
   pointsEarned: number
   maxPoints: number
   isAboveAverage: boolean
}

export interface SalesPipelineData {
   proTip: {
      title: string
      content: string
   }
   alertMetrics: AlertMetrics
   leadData: LeadData[]
   performanceMetrics: PerformanceMetrics
   dateRange: string
}

export const defaultSalesPipelineData: SalesPipelineData = {
   proTip: {
      title: 'Pro Tip',
      content:
         'Implementing an effective lead nurturing strategy can have a huge impact on the results of your inbound marketing strategy, customer loyalty, customer retention, revenue, and more.',
   },
   alertMetrics: {
      title: 'Lead Funnel Analysis',
      question: 'Are new leads getting trapped at the top of the funnel?',
      alertPercentage: '46% new, non-staged leads',
      alertType: 'error',
      description:
         'You have more New, Non-staged Leads after 48 hours than top performing teams. Forward flow can help remind your agents of necessary outreach and increase your CRM hygiene.',
      dateRange: '04/01/2024 - 04/30/2024',
   },
   leadData: [
      { name: 'Top of Funnel', value: 395, percentage: 47, color: '#FF6B6B' },
      { name: 'Long Nurture', value: 208, percentage: 25, color: '#4285F4' },
      { name: 'Short Nurture', value: 120, percentage: 14, color: '#A8C5FF' },
      {
         name: 'Active Buyer/Seller',
         value: 2,
         percentage: 0.2,
         color: '#34D399',
      },
      { name: 'Closed', value: 1, percentage: 0.1, color: '#FBBF24' },
      {
         name: 'Trash/Unmapped Stage',
         value: 109,
         percentage: 13,
         color: '#9CA3AF',
      },
   ],
   performanceMetrics: {
      totalLeads: 835,
      appointmentsSet: 54,
      appointmentRate: 6.5,
      comparisonRate: 3.5,
      pointsEarned: 100,
      maxPoints: 200,
      isAboveAverage: false,
   },
   dateRange: '04/01/2024 - 04/30/2024',
}

export const sampleSalesPipelineData: SalesPipelineData = {
   proTip: {
      title: 'Success Strategy',
      content:
         'Consistent follow-up within the first 24 hours of lead capture can increase conversion rates by up to 391%. Focus on speed-to-lead metrics.',
   },
   alertMetrics: {
      title: 'Conversion Analysis',
      question: 'Is your team converting leads efficiently?',
      alertPercentage: '28% conversion rate',
      alertType: 'info',
      description:
         'Your conversion rate is above industry average. Consider scaling your successful strategies to maintain this performance.',
      dateRange: '05/01/2025 - current',
   },
   leadData: [
      { name: 'Top of Funnel', value: 280, percentage: 35, color: '#FF6B6B' },
      { name: 'Long Nurture', value: 160, percentage: 20, color: '#4285F4' },
      { name: 'Short Nurture', value: 200, percentage: 25, color: '#A8C5FF' },
      {
         name: 'Active Buyer/Seller',
         value: 80,
         percentage: 10,
         color: '#34D399',
      },
      { name: 'Closed', value: 60, percentage: 7.5, color: '#FBBF24' },
      {
         name: 'Trash/Unmapped Stage',
         value: 20,
         percentage: 2.5,
         color: '#9CA3AF',
      },
   ],
   performanceMetrics: {
      totalLeads: 800,
      appointmentsSet: 120,
      appointmentRate: 15,
      comparisonRate: 5,
      pointsEarned: 180,
      maxPoints: 200,
      isAboveAverage: true,
   },
   dateRange: '05/01/2025 - current',
}

export interface LeadSourceData {
   name: string
   value: number
   color: string
}

export interface AgentPerformance {
   id: string
   name: string
   avatar?: string
   metrics: {
      outreachEffort?: number
      conversationRate?: number
      appointmentSetRate?: number
      textResponseRate?: number
   }
}

export interface TopLeadSourceData {
   topSource: {
      name: string
      percentage: number
      totalLeads: number
      qualifiedLeads: number
      description: string
   }
   leadSourceBreakdown: LeadSourceData[]
   agents: {
      bestOutreach: AgentPerformance
      bestConversationalist: AgentPerformance
      topAppointmentSetter: AgentPerformance
      bestTextResponse: AgentPerformance
      allAgents: AgentPerformance[]
   }
   proTip: {
      title: string
      message: string
      icon: string
   }
   cta: {
      primaryText: string
      secondaryText: string
      buttonText: string
   }
}

export const mockTopLeadSourceData: TopLeadSourceData = {
   topSource: {
      name: 'Listing Lead',
      percentage: 77,
      totalLeads: 30,
      qualifiedLeads: 23,
      description: 'Active Buyer/Seller AND Short Nurture',
   },
   leadSourceBreakdown: [
      { name: 'TOP OF FUNNEL', value: 6.7, color: '#FF6B6B' },
      { name: 'LONG NURTURE', value: 0, color: '#4285F4' },
      { name: 'SHORT NURTURE', value: 77, color: '#A8C5FF' },
      { name: 'ACTIVE', value: 0, color: '#FBBF24' },
      { name: 'CLOSED', value: 3.3, color: '#34D399' },
      { name: 'TRASH/UNMAPPED', value: 13, color: '#9CA3AF' },
   ],
   agents: {
      bestOutreach: {
         id: 'khaled-1',
         name: 'Khaled Sheira',
         avatar: '',
         metrics: {
            outreachEffort: 19.1,
         },
      },
      bestConversationalist: {
         id: 'khaled-2',
         name: 'Khaled Sheira',
         avatar: '',
         metrics: {
            conversationRate: 1.9,
         },
      },
      topAppointmentSetter: {
         id: 'khaled-3',
         name: 'Khaled Sheira',
         avatar: '',
         metrics: {
            appointmentSetRate: 20,
         },
      },
      bestTextResponse: {
         id: 'tyree-1',
         name: 'Tyree Dicks',
         avatar: '',
         metrics: {
            textResponseRate: 76,
         },
      },
      allAgents: [],
   },
   proTip: {
      title: 'Pro Tip',
      message:
         'When you add your Transaction data from your TMS, Forward flow Pro can provide ROI data for your lead sources!',
      icon: '💡',
   },
   cta: {
      primaryText:
         'The full Forward flow product can track your hottest lead sources weekly, monthly, or yearly!',
      secondaryText:
         "Want to see who your best agents are for ALL lead sources? Forward flow will have this information when it's released in Q2 2023.",
      buttonText: 'Book a demo',
   },
}

export interface OverviewCardData {
   id: string
   title: string
   titleColor: string
   bgColor: string
   borderColor: string
   value: string | number
   subtitle?: string
   description: string
   iconType:
      | 'clock'
      | 'sources'
      | 'calendar'
      | 'plant'
      | 'phone'
      | 'trophy'
      | 'warning'
      | 'star'
   status: 'good' | 'warning' | 'critical' | 'neutral'
   additionalData?: {
      timeFrame?: string
      agent?: string
      source?: string
      leadCount?: number
      percentage?: number
      days?: string
   }
}

export interface OverviewData {
   lastUpdated: string
   cards: OverviewCardData[]
}

export const overviewData: OverviewData = {
   lastUpdated: '2025-06-24T10:30:00Z',
   cards: [
      {
         id: 'contact-24h',
         title: '% of Leads Contacted in 24 Hours',
         titleColor: 'text-green-400',
         bgColor: 'bg-[#1f2937]',
         borderColor: '#374151',
         value: 85,
         description: 'of all top of funnel leads within 24 hours',
         iconType: 'clock',
         status: 'good',
         additionalData: {
            timeFrame: '24 hours',
         },
      },
      {
         id: 'best-sources',
         title: 'Best Lead Sources',
         titleColor: 'text-green-400',
         bgColor: 'bg-[#1f2937]',
         borderColor: '#374151',
         value: 'Multiple Sources',
         description:
            'The lead sources generating the most appointments are Referral, Listing Lead, zbuyer.com',
         iconType: 'sources',
         status: 'good',
         additionalData: {
            source: 'Referral, Listing Lead, zbuyer.com',
         },
      },
      {
         id: 'appointment-percentage',
         title: 'Appointment Set %',
         titleColor: 'text-red-400',
         bgColor: 'bg-[#1f2937]',
         borderColor: '#374151',
         value: 6.5,
         description:
            'of leads created in the last 30 day period have appointments set',
         iconType: 'calendar',
         status: 'critical',
         additionalData: {
            leadCount: 835,
            percentage: 6.5,
            timeFrame: '30 days',
         },
      },
      {
         id: 'leads-attention',
         title: 'Leads Needing Attention',
         titleColor: 'text-gray-400',
         bgColor: 'bg-[#1f2937]',
         borderColor: '#374151',
         value: 225,
         description:
            'leads not contacted in 60-90 days in active and lead nurture stages',
         iconType: 'plant',
         status: 'warning',
         additionalData: {
            leadCount: 225,
            days: '60-90',
         },
      },
      {
         id: 'best-call-time',
         title: 'Best Time to Call',
         titleColor: 'text-green-400',
         bgColor: 'bg-[#1f2937]',
         borderColor: '#374151',
         value: 'Wednesday at 12PM',
         description: 'The best time to call your leads',
         iconType: 'phone',
         status: 'good',
         additionalData: {
            timeFrame: 'Wednesday at 12PM',
         },
      },
      {
         id: 'top-closer',
         title: 'Top Appointment Closer',
         titleColor: 'text-green-400',
         bgColor: 'bg-[#1f2937]',
         borderColor: '#374151',
         value: 'Tyree Dicks',
         description:
            "The agent that's created the most appointments with the fewest number of conversations",
         iconType: 'trophy',
         status: 'good',
         additionalData: {
            agent: 'Tyree Dicks',
         },
      },
      {
         id: 'struggling-connect',
         title: 'Struggling to Connect',
         titleColor: 'text-red-400',
         bgColor: 'bg-[#1f2937]',
         borderColor: '#374151',
         value: 'Emily G',
         description:
            "The agent that's struggling the most to turn outbound calls into conversations",
         iconType: 'warning',
         status: 'critical',
         additionalData: {
            agent: 'Emily G',
         },
      },
      {
         id: 'top-source-appointments',
         title: 'Source with Most Appointments',
         titleColor: 'text-green-400',
         bgColor: 'bg-[#1f2937]',
         borderColor: '#374151',
         value: 'Referral',
         description:
            "The lead source that's given your team the most appointments in the last 30 days",
         iconType: 'star',
         status: 'good',
         additionalData: {
            source: 'Referral',
            timeFrame: '30 days',
         },
      },
   ],
}
